import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { Role } from '@em/shared';
import { useExceptions, useMessages } from '../api';
import { useAuth, useSession } from '../auth/AuthContext';
import { ChangePasswordDialog } from '../auth/ChangePassword';
import { ROLE_LABELS } from './format';

interface NavItem {
  to: string;
  label: string;
  min: Role;
  end?: boolean;
  badge?: ReactNode;
}

export function Layout() {
  const session = useSession();
  const { can, signOut } = useAuth();
  const [menu, setMenu] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const exceptions = useExceptions({ status: 'open' });
  const actionable = exceptions.data?.filter((x) => x.severity !== 'info').length ?? 0;
  const unreadMessages = useMessages().data?.filter((n) => !n.readAt).length ?? 0;

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menu]);

  const items: NavItem[] = [
    { to: '/', label: 'Exceptions', min: 'supervisor', end: true, badge: actionable > 0 ? <span className="count" aria-label={`${actionable} open`}>{actionable}</span> : null },
    { to: '/messages', label: 'Messages', min: 'supervisor', badge: unreadMessages > 0 ? <span className="count" aria-label={`${unreadMessages} unread`}>{unreadMessages}</span> : null },
    { to: '/live', label: 'Live board', min: 'supervisor' },
    { to: '/periods', label: 'Pay periods', min: 'supervisor' },
    { to: '/corrections', label: 'Corrections', min: 'supervisor' },
    { to: '/rosters', label: 'Rosters', min: 'supervisor' },
    { to: '/employees', label: 'Employees', min: 'supervisor' },
    { to: '/rules', label: 'Pay rules', min: 'payroll_admin' },
    { to: '/reports', label: 'Reports', min: 'supervisor' },
    { to: '/settings', label: 'Settings', min: 'payroll_admin' },
  ];

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <img src="/euro-marble-logo.png" alt={session.businessName} width={180} height={72} />
          <span>Time and attendance</span>
        </div>
        <nav aria-label="Main">
          {items
            .filter((i) => can(i.min))
            .map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end}>
                {i.label}
                {i.badge}
              </NavLink>
            ))}
        </nav>
        <div className="user" ref={menuRef}>
          <button aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
            <strong>{session.user.name}</strong>
            <small>{ROLE_LABELS[session.role]}</small>
          </button>
          {menu && (
            <div className="menu" role="menu">
              <button
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  setPwOpen(true);
                }}
              >
                Change password
              </button>
              <button role="menuitem" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>
      <main className="main" id="main">
        <Outlet />
      </main>
      {pwOpen && <ChangePasswordDialog onClose={() => setPwOpen(false)} />}
    </div>
  );
}

/** Shown when a route needs a higher role than the user holds. */
export function RequireRole({ min, children }: { min: Role; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(min))
    return (
      <div className="card">
        <div className="empty">
          <strong>Not available for your role</strong>
          This page is for {ROLE_LABELS[min].toLowerCase()}s and above.
        </div>
      </div>
    );
  return <>{children}</>;
}
